import React from 'react';
import { RenderRequest, RenderResponse, DynamicRenderResponse, PreviewHoverPayload, PreviewExportHandle, ExportAction, ExportResult, TextField, DataScenario } from 'types/apis/cxEditor';
import { useGlobalConfig } from 'context/GlobalContext';
import { createCxApiClient } from 'framework/http/CxEditorClient';
import { getApiBaseUrl, ApiModule } from 'framework/http/ApiBaseUrlFactory';
import { useDebounceEffect } from 'hooks/useDebounceEffect';
import { PreviewFrame } from './PreviewFrame';
import { Box, Typography, CircularProgress } from '@mui/material';
import { Button } from '@mui/material';
import { ErrorOutline, Replay } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { selectProjectAttributes } from 'store/dataReducers/applicationScopeSlice';
import { selectDataScenariosByKey } from 'store/dataReducers/dataScenariosSlice';
import { parseHtmlSections, SECTION_ID_TO_ELEMENT_KEY_MAP } from 'utils/html-section-parser';
import { resolveScenarioId } from 'utils/cx-editor';
import { isWebWidgetTemplate } from 'constants/web-widget-mapping';
import { isHerTemplate, prepareHerSrcDoc } from 'utils/her-preview-url';

function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .trim();
}

function substituteVariables(text: string, data: Record<string, unknown>): string {
  let result = stripHtml(text);
  // {{var}} before {var} to avoid partial match
  result = result.replace(/\{\{([a-zA-Z_][a-zA-Z0-9_.]*)\}\}/g, (m, k) => data[k] !== undefined ? String(data[k]) : m);
  // ${var} and {var}
  result = result.replace(/\$?\{([a-zA-Z_][a-zA-Z0-9_.]*)\}/g, (m, k) => data[k] !== undefined ? String(data[k]) : m);
  // %var%
  result = result.replace(/%([a-zA-Z_][a-zA-Z0-9_]*)%/g, (m, k) => data[k] !== undefined ? String(data[k]) : m);
  return result;
}

function parsePayload(raw: unknown): Record<string, unknown> {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) as Record<string, unknown>; } catch { return {}; }
  }
  return raw as Record<string, unknown>;
}

type Props = { 
  request: RenderRequest | null; 
  rightRequest?: RenderRequest | null;
  previewMode?: 'desktop' | 'mobile';
  channel?: string;
  desktopPreviewMaxWidth?: number;
  isFullTemplateSelected?: boolean;
  projectId?: string | null;
  elementKey?: string; // Current selected element/section
  onSectionsParsed?: (sections: Array<{ elementKey: string; displayName: string }>) => void; // Callback to update element dropdown
  onRenderComplete?: (templateKey: string) => void; // Callback when render completes to trigger template/scenario fetch
  onHtmlChange?: (html: string) => void; // Callback to expose rendered HTML to parent
  onLoadingChange?: (loading: boolean) => void; // Callback to indicate preview is busy (used to disable UI actions)
  variant?: { scenario?: string } | null; // Variant with scenario for Web widgets
  onWebWidgetFinished?: (templateKey: string, result: { success: boolean; error?: string }) => void; // Web widget render completion
  onPreviewHover?: (payload: PreviewHoverPayload | null) => void; // Preview hover for inspector matching
  onPreviewHoverPin?: (payload: PreviewHoverPayload) => void; // Called when user pins hover highlight via "Fix it" chip
  hoverInvestigateEnabled?: boolean;
  showColorTooltip?: boolean; // Show color hex tooltip in preview (only when inspector colors tab active)
  smsTextFields?: TextField[]; // Text fields from inspector panel for SMS preview
  onRetryRender?: () => void; // Callback to retry render when preview fails
};

export const PreviewArea = React.forwardRef<PreviewExportHandle, Props>(({
  request,
  rightRequest,
  previewMode = 'desktop',
  channel = 'Email',
  desktopPreviewMaxWidth,
  isFullTemplateSelected = false,
  projectId,
  elementKey = 'Full Template',
  onSectionsParsed,
  onRenderComplete,
  onHtmlChange,
  onLoadingChange,
  variant,
  onWebWidgetFinished,
  onPreviewHover,
  onPreviewHoverPin,
  hoverInvestigateEnabled = false,
  showColorTooltip = false,
  smsTextFields = [],
  onRetryRender,
}, ref) => {
  const previewFrameRef = React.useRef<PreviewExportHandle>(null);

  React.useImperativeHandle(ref, () => ({
    exportPreview: (action: ExportAction): Promise<ExportResult> => {
      if (!previewFrameRef.current) return Promise.resolve({ ok: false, reason: 'not-ready' });
      return previewFrameRef.current.exportPreview(action);
    },
  }), []);

  const { rc } = useGlobalConfig();
  const selectedProjectId = useSelector(selectSelectedPilotId);
  const projectAttributes = useSelector(selectProjectAttributes);
  const effectiveProjectId = projectId || selectedProjectId;

  const fetchImage = React.useCallback(async (src: string): Promise<string | null> => {
    const api = createCxApiClient(rc, getApiBaseUrl(ApiModule.Wysiwyg));
    return api.proxyImage(src);
  }, [rc]);

  // Extract fuel_type from projectAttributes (similar to InspectorPanel)
  const fuelType = React.useMemo(() => {
    let fuelType = 'ELECTRIC'; // Default fallback
    if (projectAttributes?.fuel_type) {
      // Handle different formats: string, array, or comma-separated
      if (typeof projectAttributes.fuel_type === 'string') {
        // If comma-separated, take the first one
        const fuelTypes = projectAttributes.fuel_type.split(',').map(f => f.trim());
        fuelType = fuelTypes[0]?.toUpperCase() || 'ELECTRIC';
      } else if (Array.isArray(projectAttributes.fuel_type)) {
        const fuelTypeArray = projectAttributes.fuel_type as any[];
        if (fuelTypeArray.length > 0) {
          fuelType = String(fuelTypeArray[0]).toUpperCase();
        }
      }
    }
    return fuelType;
  }, [projectAttributes]);
  
  const [fullHtml, setFullHtml] = React.useState<string>(''); // Store full rendered HTML
  const [html, setHtml] = React.useState<string>(''); // Filtered HTML based on elementKey
  const [loading, setLoading] = React.useState(false);
  const [subject, setSubject] = React.useState<string>('');
  const [renderError, setRenderError] = React.useState<string | null>(null);
  const [herPreviewHtml, setHerPreviewHtml] = React.useState<string | null>(null); // HER preview HTML rendered via srcdoc
  const [htmlB, setHtmlB] = React.useState<string>('');
  const [loadingB, setLoadingB] = React.useState(false);
  const [subjectB, setSubjectB] = React.useState<string>('');
  const abortRef = React.useRef<AbortController | null>(null);
  const abortRefB = React.useRef<AbortController | null>(null);
  const abortRefSms = React.useRef<AbortController | null>(null);
  const [smsRenderedFields, setSmsRenderedFields] = React.useState<Array<{ id: string; label: string; value: string }>>([]);
  const [smsLoading, setSmsLoading] = React.useState(false);
  
  // Helper function to get sectionNames array from elementKey
  // Returns empty array for Full Template, or array with technical section name
  // This function works without HTML by using SECTION_ID_TO_ELEMENT_KEY_MAP for reverse lookup
  const getSectionNames = React.useCallback((elementKey: string): string[] => {
    if (elementKey === 'Full Template') {
      return [];
    }
    
    // Check if elementKey is already a technical section ID (exists as key in SECTION_ID_TO_ELEMENT_KEY_MAP)
    // This handles cases where EditorToolbar passes technical section IDs directly (e.g., "MONTHLY_SUMMARY_V2")
    if (SECTION_ID_TO_ELEMENT_KEY_MAP[elementKey]) {
      return [elementKey];
    }
    
    // Reverse lookup: Find all technical section IDs that map to this display name
    // Prefer higher version numbers (e.g., V4 > V3 > V2)
    const matchingTechnicalIds: string[] = [];
    for (const [technicalId, displayName] of Object.entries(SECTION_ID_TO_ELEMENT_KEY_MAP)) {
      if (displayName === elementKey) {
        matchingTechnicalIds.push(technicalId);
      }
    }
    
    if (matchingTechnicalIds.length > 0) {
      // Sort by version number (higher first) and return the first one
      matchingTechnicalIds.sort((a, b) => {
        const versionA = parseInt(a.match(/_[Vv](\d+)/)?.[1] || '0');
        const versionB = parseInt(b.match(/_[Vv](\d+)/)?.[1] || '0');
        return versionB - versionA; // Higher version first
      });
      return [matchingTechnicalIds[0]];
    }
    
    // Final fallback: if elementKey might be a technical section ID that's not in mapping,
    // or we can't determine the technical section ID, return empty array (backend will handle full template)
    // This is safe because backend can handle empty array for full template
    return [];
  }, []);
  
  // Client-side filtering removed - API will handle filtering based on sectionNames

  // Extract primitive values from request to use as stable dependencies
  const templateKey = request?.templateKey || null;
  // undefined → request not yet set (use project fuelType fallback)
  // null     → request present but fuelType is '' i.e. no-fuel template (no fuel filter)
  // string   → specific fuel type
  const requestFuelType = request != null ? (request.fuelType || null) : undefined;
  const scenario = request?.variant?.scenario || null;
  const locale = request?.variant?.locale || 'en_US';
  const refreshKey = request?.refreshKey ?? null;

  const isWebWidget = channel === 'Web' && isWebWidgetTemplate(templateKey);
  const isHer = channel === 'HER' && isHerTemplate(templateKey);

  // Redux-cached scenarios for this pilot/fuelType/locale — passed to resolveScenarioId to skip
  // a redundant API call during the transient window where scenario==='default' hasn't been aligned yet.
  const cachedScenariosForResolve = useSelector((state: any) =>
    selectDataScenariosByKey(
      state,
      effectiveProjectId || '',
      requestFuelType !== undefined ? (requestFuelType || '') : fuelType,
      locale
    )
  );

  // Surface a single "busy" signal to parent (used to disable section/scenario/template changes).
  React.useEffect(() => {
    onLoadingChange?.(loading || loadingB || smsLoading);
  }, [loading, loadingB, smsLoading, onLoadingChange]);

  // SMS preview: fetch scenario payload and substitute variables into text fields
  useDebounceEffect(() => {
    if (channel !== 'SMS') {
      setSmsRenderedFields([]);
      setSmsLoading(false);
      return;
    }

    if (smsTextFields.length === 0) {
      setSmsRenderedFields([]);
      return;
    }

    if (!effectiveProjectId || !templateKey) {
      setSmsRenderedFields(smsTextFields.map(f => ({ id: f.id, label: f.label, value: stripHtml(f.value) })));
      return;
    }

    abortRefSms.current?.abort();
    const controller = new AbortController();
    abortRefSms.current = controller;

    let active = true;
    (async () => {
      setSmsLoading(true);
      try {
        const api = createCxApiClient(rc, getApiBaseUrl(ApiModule.Wysiwyg));
        const scenarioFuelType = requestFuelType !== undefined ? requestFuelType : fuelType;

        // Resolve scenario ID:
        // - If already a real ID (not 'default'), use it directly
        // - If 'default' or missing, fetch scenarios list and find first matching SMS scenario
        let resolvedId = (scenario && scenario !== 'default') ? scenario : null;

        if (!resolvedId) {
          const dataResp = await api.getDataScenarios(effectiveProjectId, scenarioFuelType, locale, { signal: controller.signal });
          const allScenarios: DataScenario[] = dataResp?.dataScenarios || [];
          const smsMatches = allScenarios.filter(
            s => s.productType?.toUpperCase() === 'SMS' && s.templateName === templateKey
          );
          const defaultSms = smsMatches.find(s => s.defaultScenario) || smsMatches[0];
          if (defaultSms) {
            resolvedId = defaultSms.scenarioId;
          }
        }

        if (!resolvedId) {
          // No SMS scenario found for this template — show raw values
          if (active) {
            setSmsRenderedFields(smsTextFields.map(f => ({ id: f.id, label: f.label, value: stripHtml(f.value) })));
          }
          return;
        }

        const details = await api.getDataScenarioDetails(effectiveProjectId, resolvedId, { signal: controller.signal });

        if (active && !controller.signal.aborted) {
          const mergedPayload: Record<string, unknown> = (details.payloads || []).reduce(
            (acc: Record<string, unknown>, p: any) => ({ ...acc, ...parsePayload(p.payload) }),
            {}
          );
          const rendered = smsTextFields.map(f => ({
            id: f.id,
            label: f.label,
            value: substituteVariables(f.value, mergedPayload),
          }));
          setSmsRenderedFields(rendered);
        }
      } catch (err: any) {
        const isAborted = err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || controller.signal.aborted;
        if (active && !isAborted) {
          console.error('[PreviewArea SMS] Failed to load scenario payload:', err);
          setSmsRenderedFields(smsTextFields.map(f => ({ id: f.id, label: f.label, value: stripHtml(f.value) })));
        }
      } finally {
        if (active) setSmsLoading(false);
      }
    })();
    return () => { active = false; controller.abort(); };
  }, 400, [channel, scenario, templateKey, effectiveProjectId, locale, fuelType, requestFuelType, smsTextFields, rc]);

  
  // API call when template, scenario, refreshKey, or elementKey changes
  // elementKey changes trigger new API call with updated sectionNames
  useDebounceEffect(() => {
    // For Web widget templates, skip CX render API – widgets SDK will handle rendering
    if (isWebWidget) {
      abortRef.current?.abort();
      setFullHtml('');
      setHtml('');
      setSubject('');
      setLoading(false);
      return;
    }

    if (!request || !effectiveProjectId || !templateKey || !scenario) {
      abortRef.current?.abort();
      setFullHtml(''); 
      setHtml('');
      setSubject('');
      setLoading(false);
      return;
    }

    // Cancel any previous in-flight request before starting a new one.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    let active = true;
    (async () => {
      setHtml('');
      setFullHtml('');
      setRenderError(null);
      setLoading(true);
      try {
        const api = createCxApiClient(rc, getApiBaseUrl(ApiModule.Wysiwyg));
        // undefined = no request yet → fall back to project fuelType; null/'string' = use as-is
        const scenarioFuelType = requestFuelType !== undefined ? requestFuelType : fuelType;

        // Resolve "default" scenario to actual scenario ID from data scenarios API.
        // Pass Redux-cached scenarios as fast path to avoid a redundant API call.
        const resolvedScenarioId = await resolveScenarioId(
          scenario || 'default',
          templateKey || '',
          channel || 'Email',
          effectiveProjectId || '',
          scenarioFuelType,
          locale,
          (pilot_id, fuel_type, locale, signal) => api.getDataScenarios(pilot_id, fuel_type, locale, { signal }),
          controller.signal,
          cachedScenariosForResolve.length > 0 ? cachedScenariosForResolve : undefined
        );

        // Guard: abort may have fired while resolveScenarioId's internal fetch was in flight
        if (controller.signal.aborted || !active) return;
        
        // Note: resolvedScenarioId is the actual scenario ID (e.g., "MS_1", "MS_2"), not "default"
        // Backend /v1/cx/render expects: { projectId, scenarioId, uuid?, sectionNames?, productType? }
        // The scenarioId should be resolved and contain template information
        // sectionNames: empty array for Full Template, or array with technical section name
        // Get sectionNames based on current elementKey (don't use fullHtml for lookup since we're calling API)
        const sectionNamesForApi = getSectionNames(elementKey);
        // Cluster level only: pass clusterReferences. Project level unchanged (no clusterReferences).
        const isEmailWithCluster = channel === 'Email' && request?.variant?.hierarchy?.startsWith('Cluster:');
        const clusterRef = request?.variant?.hierarchy?.replace(/^Cluster:/, '') || '';
        const clusterReferencesForApi = isEmailWithCluster && clusterRef ? [clusterRef] : undefined;

        const resp: DynamicRenderResponse = await api.renderDynamic({
          projectId: effectiveProjectId || '',
          scenarioId: resolvedScenarioId,
          uuid: '', // UUID is optional, backend will get it from project
          ...(isHer && { productType: 'HER' }),
          sectionNames: sectionNamesForApi,
          ...(clusterReferencesForApi && clusterReferencesForApi.length > 0 && { clusterReferences: clusterReferencesForApi }),
          locale
        }, { signal: controller.signal });
        if (active && !controller.signal.aborted) {
          
          if (isHer && resp.herPreviewUrl) {
            // Fetch full HTML and prepare it for srcDoc rendering (hover needs same-origin DOM access)
            try {
              const herHtml = await api.fetchHerPreviewHtml(resp.herPreviewUrl, effectiveProjectId || '', { signal: controller.signal });
              if (active && !controller.signal.aborted) {
                setHerPreviewHtml(prepareHerSrcDoc(herHtml, resp.herPreviewUrl));
                setFullHtml('');
                setHtml('');
                setSubject('');
              }
            } catch (herErr: any) {
              const herAborted =
                herErr?.code === 'ERR_CANCELED' ||
                herErr?.name === 'CanceledError' ||
                (controller.signal.aborted as boolean);
              if (active && !herAborted) {
                console.error('[PreviewArea] Failed to fetch HER preview HTML', herErr);
                setHerPreviewHtml(null);
                const errorHtml = '<div style="padding:12px;color:crimson">Failed to load HER preview</div>';
                setFullHtml(errorHtml);
                setHtml(errorHtml);
              }
            }
          } else {
            const renderedHtml = resp.html || '';
            // API returns filtered HTML based on sectionNames, so use it directly (no client-side filtering)
            setFullHtml(renderedHtml); // Store full HTML (may be filtered by backend)
            setHtml(renderedHtml); // Use API response directly - backend handles filtering
            setSubject(resp.subject || ''); // renderDynamic returns subject
            setHerPreviewHtml(null); // Clear HER HTML for non-HER templates
          }
          
          const renderedHtml = resp.html || '';
          // Expose HTML to parent (for InspectorPanel to get technical section IDs)
          // Note: For section-specific renders, HTML may be filtered, but we still expose it
          if (onHtmlChange && renderedHtml) {
            onHtmlChange(renderedHtml);
          }
          
          // Parse sections from HTML and notify parent
          // Only parse when rendering Full Template to get all available sections
          if (renderedHtml && onSectionsParsed && elementKey === 'Full Template') {
            const { sectionIds } = parseHtmlSections(renderedHtml);
            // Create elements array with "Full Template" as first option
            // Use sectionIds (technical IDs) as elementKey, and map them to display names
            const sections = [
              { elementKey: 'Full Template', displayName: 'Full Template' },
              ...sectionIds.map(sectionId => ({
                elementKey: sectionId, // Use technical ID (e.g., "DF_SHC_BARGRAPH_COMPARISON")
                displayName: SECTION_ID_TO_ELEMENT_KEY_MAP[sectionId] || sectionId // Map to display name
              }))
            ];
            onSectionsParsed(sections);
          }
          
          // Notify parent that render is complete - trigger template/scenario fetch
          if (onRenderComplete && templateKey) {
            onRenderComplete(templateKey);
          }
        }
      } catch (err: any) {
        const isAborted =
          err?.code === 'ERR_CANCELED' ||
          err?.name === 'CanceledError' ||
          controller.signal.aborted === true;
        if (active && !isAborted) {
          console.error('[PreviewArea] API call failed', err);
          setFullHtml('');
          setHtml('');
          setSubject('');
          const rootCause = err?.response?.data?.rootCause;
          setRenderError(rootCause || 'Failed to render preview');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();
    return () => {
      active = false;
      controller.abort();
    };
  }, 400, [templateKey, scenario, locale, effectiveProjectId, elementKey, refreshKey, isWebWidget, fuelType, requestFuelType, channel, request?.variant?.hierarchy]); // include variant.hierarchy for EMAIL cluster

  // Extract primitive values from rightRequest for stable dependencies
  const rightTemplateKey = rightRequest?.templateKey || null;
  const rightFuelType = rightRequest != null ? (rightRequest.fuelType || null) : undefined;
  const rightScenario = rightRequest?.variant?.scenario || null;
  const rightLocale = rightRequest?.variant?.locale || null;
  
  useDebounceEffect(() => {
    let active = true;
    (async () => {
      // For Web widget templates, skip CX render API – widgets SDK will handle rendering
      if (channel === 'Web' && isWebWidgetTemplate(rightTemplateKey)) {
        abortRefB.current?.abort();
        setHtmlB('');
        setSubjectB('');
        setLoadingB(false);
        return;
      }

      if (!rightRequest || !effectiveProjectId || !rightTemplateKey || !rightScenario) { 
        abortRefB.current?.abort();
        setHtmlB(''); 
        setSubjectB('');
        setLoadingB(false);
        return; 
      }
      
      abortRefB.current?.abort();
      const controller = new AbortController();
      abortRefB.current = controller;
      
      setLoadingB(true);
      try {
        const api = createCxApiClient(rc, getApiBaseUrl(ApiModule.Wysiwyg));
        const rightScenarioFuelType = rightFuelType !== undefined ? rightFuelType : fuelType;

        // Resolve "default" scenario to actual scenario ID from data scenarios API.
        // Pass Redux-cached scenarios as fast path to avoid a redundant API call.
        const resolvedScenarioId = await resolveScenarioId(
          rightScenario || 'default',
          rightTemplateKey || '',
          channel || 'Email',
          effectiveProjectId || '',
          rightScenarioFuelType,
          rightLocale || 'en_US',
          (pilot_id, fuel_type, locale, signal) => api.getDataScenarios(pilot_id, fuel_type, locale, { signal }),
          controller.signal,
          cachedScenariosForResolve.length > 0 ? cachedScenariosForResolve : undefined
        );

        // Guard: abort may have fired while resolveScenarioId's internal fetch was in flight
        if (controller.signal.aborted || !active) return;
        
        // Backend /v1/cx/render expects: { projectId, scenarioId, uuid?, sectionNames?, productType? }
        // sectionNames: empty array for Full Template, or array with technical section name
        // Get sectionNames based on current elementKey (don't use fullHtml for lookup since we're calling API)
        const sectionNamesForApi = getSectionNames(elementKey);
        const isRightHer = channel === 'HER' && isHerTemplate(rightTemplateKey);
        const isRightEmailWithCluster = channel === 'Email' && rightRequest?.variant?.hierarchy?.startsWith('Cluster:');
        const rightClusterRef = rightRequest?.variant?.hierarchy?.replace(/^Cluster:/, '') || '';
        const rightClusterReferencesForApi = isRightEmailWithCluster && rightClusterRef ? [rightClusterRef] : undefined;
        const resp: DynamicRenderResponse = await api.renderDynamic({
          projectId: effectiveProjectId || '',
          scenarioId: resolvedScenarioId,
          uuid: '', // UUID is optional, backend will get it from project
          sectionNames: sectionNamesForApi,
          ...(isRightHer && { productType: 'HER' }), // Pass productType for HER templates
          ...(rightClusterReferencesForApi && rightClusterReferencesForApi.length > 0 && { clusterReferences: rightClusterReferencesForApi }),
          locale: rightLocale || 'en_US'
        }, { signal: controller.signal });
        if (active && !controller.signal.aborted) {
          setHtmlB(resp.html || '');
          setSubjectB(resp.subject || ''); // renderDynamic returns subject
        }
      } catch (err) {
        const anyErr: any = err;
        const isAborted =
          anyErr?.code === 'ERR_CANCELED' ||
          anyErr?.name === 'CanceledError' ||
          controller.signal.aborted === true;
        if (active && !isAborted) {
          setHtmlB('<div style="padding:12px;color:crimson">Failed to render preview</div>');
          setSubjectB('');
        }
      } finally {
        if (active) setLoadingB(false);
      }
    })();
    return () => {
      active = false;
      abortRefB.current?.abort();
    };
  }, 400, [rightTemplateKey, rightScenario, rightLocale, effectiveProjectId, elementKey, channel, fuelType, rightFuelType, rightRequest?.variant?.hierarchy]);
  if (rightRequest) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'grid',
          gridTemplateColumns: '1fr 1px 1fr',
          gap: 2,
          p: 2,
          borderRadius: 2,
          bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
          overflow: 'hidden'
        }}
      >
        <PreviewFrame
          variantName="A"
          variant={variant || request?.variant || null}
          renderRequest={request}
          previewMode={previewMode}
          channel={channel}
          desktopPreviewMaxWidth={desktopPreviewMaxWidth}
          isFullTemplateSelected={isFullTemplateSelected}
          renderedHtml={html}
          renderedSubject={subject}
          loading={loading}
          isWebWidgetTemplate={isWebWidget}
          herPreviewHtml={herPreviewHtml}
          onWebWidgetFinished={onWebWidgetFinished}
          onPreviewHover={onPreviewHover}
          onPreviewHoverPin={onPreviewHoverPin}
          hoverInvestigateEnabled={hoverInvestigateEnabled}
          showColorTooltip={showColorTooltip}
          fetchImage={fetchImage}
        />
        <Box
          sx={{
            width: '1px',
            bgcolor: 'divider'
          }}
        />
        <PreviewFrame
          variantName="B"
          variant={variant || rightRequest?.variant || null}
          renderRequest={rightRequest}
          previewMode={previewMode}
          channel={channel}
          desktopPreviewMaxWidth={desktopPreviewMaxWidth}
          isFullTemplateSelected={isFullTemplateSelected}
          renderedHtml={htmlB}
          renderedSubject={subjectB}
          loading={loadingB}
          isWebWidgetTemplate={
            channel === 'Web' && isWebWidgetTemplate(rightTemplateKey)
          }
          herPreviewHtml={herPreviewHtml}
          onWebWidgetFinished={onWebWidgetFinished}
          onPreviewHover={onPreviewHover}
          onPreviewHoverPin={onPreviewHoverPin}
          hoverInvestigateEnabled={hoverInvestigateEnabled}
          showColorTooltip={showColorTooltip}
          fetchImage={fetchImage}
        />
      </Box>
    );
  }

  if (channel === 'SMS') {
    return (
      <Box
        sx={{
          height: '100%',
          borderRadius: 2,
          overflow: 'auto',
          bgcolor: theme => theme.palette.mode === 'dark' ? '#1c1c1e' : '#e5ddd5',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 2,
          gap: 1,
        }}
      >
        {smsLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <CircularProgress size={32} />
          </Box>
        ) : smsRenderedFields.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No SMS strings found. Select a template with SMS content.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: '100%', maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {smsRenderedFields.map(field => (
              <Box key={field.id}>
                <Typography variant="caption" sx={{ color: 'text.secondary', px: 1, display: 'block', mb: 0.5 }}>
                  {field.label}
                </Typography>
                <Box
                  sx={{
                    bgcolor: theme => theme.palette.mode === 'dark' ? '#2c2c2e' : '#ffffff',
                    borderRadius: '18px 18px 4px 18px',
                    px: 2,
                    py: 1.5,
                    ml: 'auto',
                    maxWidth: '85%',
                    width: 'fit-content',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {field.value || <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>(empty)</Box>}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        borderRadius: 2,
        p: 2,
        bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
        overflow: 'auto'
      }}
    >
      {renderError ? (
        <Box sx={{
          height: '100%',
          minHeight: 180,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
        }}>
          <ErrorOutline sx={{ fontSize: 40, color: 'error.light', opacity: 0.7 }} />
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {renderError}
          </Typography>
          {onRetryRender && (
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={<Replay />}
              onClick={() => { setRenderError(null); onRetryRender(); }}
              sx={{ mt: 0.5, borderRadius: 5, textTransform: 'none', px: 2.5 }}
            >
              Retry
            </Button>
          )}
        </Box>
      ) : (
        <PreviewFrame
          ref={previewFrameRef}
          variant={variant || request?.variant || null}
          renderRequest={request}
          previewMode={previewMode}
          channel={channel}
          desktopPreviewMaxWidth={desktopPreviewMaxWidth}
          isFullTemplateSelected={isFullTemplateSelected}
          renderedHtml={html}
          renderedSubject={subject}
          loading={loading}
          isWebWidgetTemplate={isWebWidget}
          herPreviewHtml={herPreviewHtml}
          onWebWidgetFinished={onWebWidgetFinished}
          onPreviewHover={onPreviewHover}
          onPreviewHoverPin={onPreviewHoverPin}
          hoverInvestigateEnabled={hoverInvestigateEnabled}
          showColorTooltip={showColorTooltip}
          fetchImage={fetchImage}
        />
      )}
    </Box>
  );
});
